import { zodResolver } from '@hookform/resolvers/zod'
import { Checkbox, FormControlLabel, Popover } from '@material-ui/core'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'

import Button from '@/components/button'
import { useDownloadFilesFromDemand } from '@/hooks/mutations/use-download-files-from-demand'
import { useGetDiagnosisById } from '@/hooks/queries/use-get-diagnosis-by-id'
import { useGetPlantingFailureSummaryFromDiagnosis } from '@/hooks/queries/use-get-planting-failure-summary-from-diagnosis'
import { Header } from '@/pages/diagnosis-view/components/header'
import { Shimmer } from '@/pages/diagnosis-view/components/shimmer'
import { DownloadTypes } from '@/services/get-url-to-download-file'
import { Culture } from '@/types/culture'

import Info from '../../weed/info'
import { SummaryTable } from '../summary-table'
import { useStyles } from './styles'

const downloadFilesSchema = z.object({
  planting_failure_shp: z.boolean().default(false),
  planting_failure_splitted: z.boolean().default(false),
  planting_failure_pdf: z.boolean().default(false),
  planting_failure_splitted_pdf: z.boolean().default(false),
  planting_failure_spreadsheet: z.boolean().default(false),
  planting_failure_splitted_spreadsheet: z.boolean().default(false),
  perimeter: z.boolean().default(false),
  mosaic: z.boolean().default(false),
})

type DownloadFilesFormData = z.infer<typeof downloadFilesSchema>

export interface SummaryProps {
  diagnosisId: string
  culture: Culture
}

export function Summary({ diagnosisId, culture }: SummaryProps) {
  const navigate = useNavigate()
  const classes = useStyles()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const { handleSubmit, control, watch, reset } =
    useForm<DownloadFilesFormData>({
      resolver: zodResolver(downloadFilesSchema),
    })
  const downloadFilesSelectedLength = Object.values(watch()).filter(
    Boolean,
  ).length

  const { data: diagnosis } = useGetDiagnosisById(
    'planting_failure',
    culture,
    diagnosisId,
  )

  const { data: summary } = useGetPlantingFailureSummaryFromDiagnosis(
    culture,
    diagnosisId,
    !!diagnosis,
  )
  const summaryData = summary?.summary

  const { isLoading, mutate } = useDownloadFilesFromDemand(culture, diagnosisId)

  function onSubmitFilesDownloadForm(data: DownloadFilesFormData) {
    onCloseDownloadMenu()

    Object.entries(data)
      .filter(([, checked]) => checked)
      .forEach(([fileType], index) =>
        mutate({
          type: fileType as DownloadTypes,
          waitInMs: index * 1000,
        }),
      )
  }

  function onCloseDownloadMenu() {
    setAnchorEl(null)
    reset()
  }

  return (
    <>
      <Header title='Linhas e Falha de plantio' goBack={() => navigate(-1)} />

      <div className={classes.buttonContainer}>
        <Button
          outlined
          loading={isLoading}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) =>
            setAnchorEl(e.currentTarget)
          }
          disabled={!summary}
        >
          <span className='material-symbols-outlined'>download</span>
          Exportar arquivos
          <ArrowDropDownIcon />
        </Button>
      </div>

      {!summary ? (
        <Shimmer variant='box' maxSizeBox='initial' />
      ) : (
        <SummaryTable summary={summaryData} />
      )}

      {!diagnosis ? (
        <Shimmer variant='box' maxSizeBox='20rem' />
      ) : (
        <Info
          title='Dados do diagnóstico'
          demandName={diagnosis.demandName}
          flightDate={diagnosis.flightDateLabel}
          concludedAt={diagnosis.concludedAtLabel}
          gsd={diagnosis.gsd}
          datum={diagnosis.datum}
          totalArea={summaryData?.total_area.ha}
        />
      )}
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={!!anchorEl}
        onClose={onCloseDownloadMenu}
        classes={{ paper: classes.paper }}
      >
        <form onSubmit={handleSubmit(onSubmitFilesDownloadForm)}>
          <Controller
            control={control}
            name='planting_failure_shp'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Arquivos de falhas e linhas (shapefile)'
              />
            )}
          />
          <Controller
            control={control}
            name='planting_failure_splitted'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Arquivos de falhas e linhas por talhão (shapefile)'
              />
            )}
          />
          <Controller
            control={control}
            name='planting_failure_pdf'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Mapa de falhas (PDF)'
              />
            )}
          />
          <Controller
            control={control}
            name='planting_failure_splitted_pdf'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Mapa de falhas por talhão (PDF)'
              />
            )}
          />
          <Controller
            control={control}
            name='planting_failure_spreadsheet'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Planilha de falhas'
              />
            )}
          />
          <Controller
            control={control}
            name='planting_failure_splitted_spreadsheet'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Planilha de falhas por talhão'
              />
            )}
          />
          <Controller
            control={control}
            name='perimeter'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Perímetro'
              />
            )}
          />
          <Controller
            control={control}
            name='mosaic'
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox size='small' {...field} />}
                label='Ortomosaico'
              />
            )}
          />

          <Button type='submit' disabled={!downloadFilesSelectedLength}>
            Download{' '}
            {Boolean(downloadFilesSelectedLength) &&
              `(${downloadFilesSelectedLength} ${
                downloadFilesSelectedLength > 1 ? 'arquivos' : 'arquivo'
              })`}
          </Button>
        </form>
      </Popover>
    </>
  )
}
